import { Injectable, inject } from '@angular/core';
import { Router, Route } from '@angular/router';
import { Store } from '@ngrx/store';
import * as AuthSelectors from '@app/store/auth/auth.selectors';
import { Menu } from '@/app/pages/menu-system/models/menu.model';
import { DynamicRouteGuard } from '@/app/shared/guards/dynamic-route.guard';

@Injectable({ providedIn: 'root' })
export class DynamicRouteService {
  private router = inject(Router);
  private readonly store = inject(Store);

  menus$ = this.store.select(AuthSelectors.selectMenus);
  private initializationPromise: Promise<void> | null = null;
  private isInitialized = false;

  protected currentMenus: Menu[] | null = null;

  constructor() {
    this.menus$.subscribe((menus) => (this.currentMenus = menus));
  }

  // Manual routes that should not be overridden
  private readonly manualRoutes = new Set([
    '/login',
    '/notfound',
    '/dashboard',
    '/settings/page-builder',
    '/settings/page-builder/create',
    '/settings/page-builder/edit/:id',
    '/settings/menu-management',
  ]);

  async ensureInitialized(): Promise<void> {
    if (this.isInitialized && this.currentMenus) return;
    if (this.initializationPromise) return this.initializationPromise;

    this.initializationPromise = this.initializeDynamicRoutes();
    await this.initializationPromise;
    this.isInitialized = true;
  }

  async initializeDynamicRoutes(maxRetries = 3): Promise<void> {
    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      try {
        const newMenus = this.currentMenus;

        if (!newMenus || newMenus.length === 0) {
          if (attempt === maxRetries) {
            return;
          }

          // Wait and retry with exponential backoff
          const delay = Math.min(1000 * Math.pow(2, attempt - 1), 5000);
          await new Promise((resolve) => setTimeout(resolve, delay));
          continue;
        }

        const dynamicRoutes = await this.buildDynamicRoutes(newMenus);
        this.addDynamicRoutesToRouter(dynamicRoutes);

        return;
      } catch (error) {
        console.error(
          `Error initializing dynamic routes (attempt ${attempt}/${maxRetries}):`,
          error,
        );

        if (attempt === maxRetries) {
          console.error('Failed to initialize dynamic routes after max retries');
          throw error;
        }

        // Wait before retrying
        const delay = 1000 * attempt;
        await new Promise((resolve) => setTimeout(resolve, delay));
      }
    }
  }

  async refreshDynamicRoutes(): Promise<void> {
    await this.initializeDynamicRoutes();
  }

  private async buildDynamicRoutes(menuItems: Menu[]): Promise<Route[]> {
    const routes: Route[] = [];

    // Helper function to flatten menu hierarchy including children
    const flattenMenus = (menus: Menu[]): Menu[] => {
      const flattened: Menu[] = [];

      for (const menu of menus) {
        flattened.push(menu); // Add parent menu

        // Recursively add children
        if (menu.LIST_CHILD && menu.LIST_CHILD.length > 0) {
          flattened.push(...flattenMenus(menu.LIST_CHILD));
        }
      }

      return flattened;
    };

    const allMenuItems = flattenMenus(menuItems);

    for (const item of allMenuItems) {
      if (!item.C_URL || (item.C_URL && !item.C_SCHEMA_ID)) continue;

      const route = await this.buildRouteFromMenuItem(item);
      if (route) routes.push(route);
    }

    return routes;
  }

  private async buildRouteFromMenuItem(item: Menu): Promise<Route | null> {
    if (!item.C_URL || (item.C_URL && !item.C_SCHEMA_ID)) return null;

    const cleanPath = item.C_URL.startsWith('/') ? item.C_URL.substring(1) : item.C_URL;

    return {
      path: cleanPath,
      canMatch: [DynamicRouteGuard],
      children: [
        {
          path: '',
          loadComponent: () =>
            import('../components/dynamic-page/dynamic-page.component').then(
              (m) => m.DynamicPageComponent,
            ),
          data: {
            menuItem: item,
            schemaId: item.C_SCHEMA_ID,
            title: item.C_MENU_NAME,
          },
        },
        {
          path: 'detail',
          loadComponent: () =>
            import('../components/dynamic-page-detail/dynamic-page-detail.component').then(
              (m) => m.DynamicPageDetailComponent,
            ),
          data: {
            schemaId: item.C_SCHEMA_ID,
            title: item.C_MENU_NAME,
            action: {
              key: 'pageBuilder.dynamicPage.createFor',
              params: { name: item.C_MENU_NAME },
            },
          },
        },
        {
          path: 'detail/:id',
          loadComponent: () =>
            import('../components/dynamic-page-detail/dynamic-page-detail.component').then(
              (m) => m.DynamicPageDetailComponent,
            ),
          data: {
            schemaId: item.C_SCHEMA_ID,
            title: item.C_MENU_NAME,
            action: {
              key: 'pageBuilder.dynamicPage.editFor',
              params: { name: item.C_MENU_NAME },
            },
          },
        },
      ],
    };
  }

  /**
   * Check if a dynamic route exists for the given path
   * Recursively searches through menu hierarchy including children
   * Also handles manual routes and parameterized routes
   */
  isRouteExists(path: string): boolean {
    if (!this.currentMenus) return false;

    // Helper function to match route patterns with parameters
    const matchRoutePattern = (routePattern: string, actualPath: string): boolean => {
      // Exact match
      if (routePattern === actualPath) {
        return true;
      }

      // Check if the actual path starts with the route pattern + '/detail'
      if(actualPath.startsWith(routePattern + '/detail')) {
        return true;
      }

      const patternRegex = new RegExp('^' + routePattern.replace(/:[^/]+/g, '([^/]+)') + '$');
      return patternRegex.test(actualPath);
    };

    // Helper function to check a single menu item and its children
    const checkMenuItem = (menu: Menu): boolean => {
      // Check current menu item
      if (menu.C_URL) {
        const cleanPath = menu.C_URL.startsWith('/') ? menu.C_URL.substring(1) : menu.C_URL;
        const inputCleanPath = path.startsWith('/') ? path.substring(1) : path;

        // Try exact match first
        if (cleanPath === inputCleanPath) {
          return true;
        }

        // Try pattern matching for parameterized routes
        if (matchRoutePattern(cleanPath, inputCleanPath)) {
          return true;
        }
      }

      // Recursively check children
      if (menu.LIST_CHILD && menu.LIST_CHILD.length > 0) {
        return menu.LIST_CHILD.some((child) => checkMenuItem(child));
      }

      return false;
    };

    // Check all root menus and their children
    return this.currentMenus.some((menu) => checkMenuItem(menu));
  }

  private addDynamicRoutesToRouter(dynamicRoutes: Route[]): void {
    if (dynamicRoutes.length === 0) {
      return;
    }

    try {
      const config = this.router.config;
      const layoutRouteIndex = config.findIndex((route) => route.path === '' && route.children);

      if (layoutRouteIndex === -1) {
        console.error(
          'Main layout route not found - cannot add dynamic routes. Router config:',
          config,
        );
        return;
      }

      const layoutRoute = config[layoutRouteIndex];
      if (!layoutRoute.children) {
        layoutRoute.children = [];
      }

      let addedCount = 0;
      let updatedCount = 0;

      for (const dynamicRoute of dynamicRoutes) {
        try {
          const existingIndex = layoutRoute.children.findIndex(
            (child) => child.path === dynamicRoute.path,
          );

          if (existingIndex !== -1) {
            layoutRoute.children[existingIndex] = dynamicRoute;
            updatedCount++;
          } else {
            // Check if this is a manual route that should not be overridden
            const routePath = dynamicRoute.path;
            if (!routePath) {
              continue;
            }

            const isManualRoute = this.manualRoutes.has(routePath);
            if (isManualRoute) {
              continue;
            }

            const wildcardIndex = layoutRoute.children.findIndex((child) => child.path === '**');
            if (wildcardIndex !== -1) {
              layoutRoute.children.splice(wildcardIndex, 0, dynamicRoute);
            } else {
              layoutRoute.children.push(dynamicRoute);
            }
            addedCount++;
          }
        } catch (routeError) {
          console.error(
            `Error processing dynamic route ${dynamicRoute.path || 'unknown'}:`,
            routeError,
          );
          // Continue with other routes
        }
      }

      this.router.resetConfig(config);
    } catch (error) {
      console.error('Critical error adding dynamic routes to router:', error);
      throw error; // Re-throw to let caller handle
    }
  }
}
